import React, { useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { fetchDashboardPayload, fetchDashboardPayloadLite } from '../utils/api';
import DashboardView from '../components/DashboardView';

const DashboardPage = () => {
  const { csvData, columnTypes, companyName, hasUploaded, isDemo } = useAppContext();

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const hasData = useMemo(() => Array.isArray(csvData) && csvData.length > 0, [csvData]);

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      setErr(null);
      setLoading(true);
      try {
        if (hasData && isDemo) {
          // Only in demo do we POST rows; real uploads use server-side app_state
          const inferredName =
            (companyName && companyName.trim()) ||
            (() => {
              try {
                const first = csvData?.[0] || {};
                return first.Company || first.CompanyName || first.company || '';
              } catch { return ''; }
            })() || 'Dataset';

          const p = await fetchDashboardPayload({
            rows: csvData,
            columnTypes: columnTypes || {},
            companyName: inferredName,
          });
          if (!ignore) setPayload(p);
        } else {
          // Fast path: read from server-side app_state (upload route already stored df)
          try {
            const p = await fetchDashboardPayloadLite();
            if (!ignore) setPayload(p);
          } catch (e1) {
            const code = Number(String(e1?.message || '').replace(/\D/g, '')) || 0;
            if (code === 404 || code === 503) {
              await new Promise((r) => setTimeout(r, 250));
              try {
                const p2 = await fetchDashboardPayloadLite();
                if (!ignore) setPayload(p2);
              } catch (e2) {
                if (!ignore) setErr(e2?.message || 'Failed to build dashboard payload');
              }
            } else {
              if (!ignore) setErr(e1?.message || 'Failed to build dashboard payload');
            }
          }
        }


      } catch (e) {
        // Only surface errors from the client-hydrated (POST) path here.
        // The lite path handles its own retry + error above.
        if (!ignore && hasData) setErr(e?.message || 'Failed to build dashboard payload');
      } finally {

        if (!ignore) setLoading(false);
      }
    };

    load();
    return () => { ignore = true; };
  }, [hasData, csvData, columnTypes, companyName, isDemo, hasUploaded]);


  if (!payload && loading) {
    const label = hasUploaded && !isDemo ? 'Loading your data…' : 'Loading demo data…';
    return (
      <div style={{ height: '100vh', display: 'grid', placeItems: 'center' }} aria-busy="true">
        {label}
      </div>
    );
  }

  return (
    <>
      {loading && (
        <div style={{ height: '100vh', display: 'grid', placeItems: 'center' }} aria-busy="true">
          <div>Building your dashboard…</div>
        </div>
      )}
      {err && (
        <div role="alert" style={{ height: '100vh', display: 'grid', placeItems: 'center' }}>
          <div>Couldn’t load dashboard: {String(err)}</div>
        </div>
      )}
      {!loading && !err && payload && <DashboardView payload={payload} />}
    </>
  );
};

export default DashboardPage;
